import type { Candle } from '../types';

const DEFAULT_RSI_LENGTH = 12;
const DEFAULT_STOCH_LENGTH = 14;
const DEFAULT_K_SMOOTHING = 1;
const DEFAULT_D_SMOOTHING = 3;
const DEFAULT_BOLLINGER_LENGTH = 20;
const DEFAULT_BOLLINGER_MULTIPLIER = 2;
const DEFAULT_ATR_LENGTH = 14;
const DEFAULT_BREAKOUT_VOLUME_EMA_LENGTH = 20;
const DEFAULT_USE_BREAKOUT_VOLUME_FILTER = true;
const DEFAULT_BREAKOUT_VOLUME_MULTIPLIER = 1.4;
const DEFAULT_ALLOW_UPPER_BAND_BRIDGE_YELLOW = true;
const DEFAULT_PIVOT_LEFT = 2;
const DEFAULT_PIVOT_RIGHT = 2;

export type ColorProSignalCode = 'B' | 'C' | 'R' | 'H';
export type ColorProSignalDirection = 'bullish' | 'bearish';

export type ColorProCandleState =
  | 'bullishBreakout'
  | 'bullishUnconfirmedReversal'
  | 'bullishTrendWeak'
  | 'bullishTrendStrong'
  | 'bearishBreakdown'
  | 'bearishPivot'
  | 'bearishTrend'
  | 'stalledAfterBreakout'
  | 'neutralPivotMinorBullishBias'
  | 'neutralNoTrend';

export interface ColorProSignal {
  index: number;
  code: ColorProSignalCode;
  direction: ColorProSignalDirection;
  price: number;
}

export interface ColorProTradingSuiteV31OverlayResult {
  candleStates: ColorProCandleState[];
  candleColors: (string | undefined)[];
  bollingerUpper: (number | undefined)[];
  bollingerBasis: (number | undefined)[];
  bollingerLower: (number | undefined)[];
  dailyResistance: (number | undefined)[];
  dailySupport: (number | undefined)[];
  weeklyResistance: (number | undefined)[];
  weeklySupport: (number | undefined)[];
  pivotPoints: (number | undefined)[];
  signals: ColorProSignal[];
  info: string;
}

interface ColorProTradingSuiteV31Params {
  candles: Candle[];
  rsiLength?: number;
  stochLength?: number;
  kSmoothing?: number;
  dSmoothing?: number;
  bollingerLength?: number;
  bollingerMultiplier?: number;
  useBreakoutVolumeFilter?: boolean;
  breakoutVolumeMultiplier?: number;
  allowUpperBandBridgeYellow?: boolean;
}

interface Pivot {
  index: number;
  value: number;
}

interface PeriodLevels {
  resistance: (number | undefined)[];
  support: (number | undefined)[];
  pivot: (number | undefined)[];
}

type ColorProBaseTrendState =
  | 'bullishTrendWeak'
  | 'bullishTrendStrong'
  | 'bearishTrend'
  | 'neutralNoTrend';

export const COLORPRO_V31_CANDLE_COLORS: Record<ColorProCandleState, string> = {
  bullishBreakout: '#4e6cff',
  bullishUnconfirmedReversal: '#f2e83a',
  bullishTrendWeak: '#9dc273',
  bullishTrendStrong: '#7cf146',
  bearishBreakdown: '#7e1f8f',
  bearishPivot: '#eb8c1d',
  bearishTrend: '#d77270',
  stalledAfterBreakout: '#8f8b1f',
  neutralPivotMinorBullishBias: '#d34ae8',
  neutralNoTrend: '#9ca7a9',
};

const RED_FAMILY_STATES = new Set<ColorProCandleState>([
  'bearishTrend',
  'bearishPivot',
  'bearishBreakdown',
]);

const GREEN_FAMILY_STATES = new Set<ColorProCandleState>([
  'bullishTrendWeak',
  'bullishTrendStrong',
  'bullishBreakout',
]);

function isLowerBollingerContext(candle: Candle, lowerBand: number | undefined): boolean {
  return lowerBand !== undefined && candle.low <= lowerBand;
}

export function applyColorProV31StateGuards(
  previousState: ColorProCandleState | undefined,
  requestedState: ColorProCandleState,
  candle: Candle,
  lowerBand: number | undefined,
  options?: {
    allowOffBandYellow?: boolean;
  },
): ColorProCandleState {
  const inLowerBollingerContext = isLowerBollingerContext(candle, lowerBand);
  const allowOffBandYellow = options?.allowOffBandYellow === true;

  if (requestedState === 'bullishUnconfirmedReversal' && !inLowerBollingerContext && !allowOffBandYellow) {
    return 'neutralNoTrend';
  }

  if (
    previousState === 'bullishUnconfirmedReversal' &&
    RED_FAMILY_STATES.has(requestedState) &&
    candle.close > candle.open
  ) {
    return 'neutralNoTrend';
  }

  if (
    previousState !== undefined &&
    RED_FAMILY_STATES.has(previousState) &&
    GREEN_FAMILY_STATES.has(requestedState)
  ) {
    return inLowerBollingerContext ? 'bullishUnconfirmedReversal' : 'neutralNoTrend';
  }

  return requestedState;
}

function ratio(numerator: number, denominator: number): number {
  if (denominator === 0) {
    return 0;
  }
  return numerator / denominator;
}

function bodyRatio(candle: Candle): number {
  return ratio(Math.abs(candle.close - candle.open), Math.max(1e-9, candle.high - candle.low));
}

function upperWickRatio(candle: Candle): number {
  const bodyTop = Math.max(candle.open, candle.close);
  return ratio(candle.high - bodyTop, Math.max(1e-9, candle.high - candle.low));
}

function lowerWickRatio(candle: Candle): number {
  const bodyBottom = Math.min(candle.open, candle.close);
  return ratio(bodyBottom - candle.low, Math.max(1e-9, candle.high - candle.low));
}

function lowestNumberInRange(values: number[], start: number, end: number): number {
  let min = Number.POSITIVE_INFINITY;
  for (let index = start; index <= end; index += 1) {
    if (values[index] < min) {
      min = values[index];
    }
  }
  return min;
}

function highestNumberInRange(values: number[], start: number, end: number): number {
  let max = Number.NEGATIVE_INFINITY;
  for (let index = start; index <= end; index += 1) {
    if (values[index] > max) {
      max = values[index];
    }
  }
  return max;
}

function lowestOptionalInRange(
  values: (number | undefined)[],
  start: number,
  end: number,
): number | undefined {
  let min = Number.POSITIVE_INFINITY;
  let seen = false;
  for (let index = start; index <= end; index += 1) {
    const value = values[index];
    if (value === undefined) {
      continue;
    }
    if (value < min) {
      min = value;
    }
    seen = true;
  }
  return seen ? min : undefined;
}

function highestOptionalInRange(
  values: (number | undefined)[],
  start: number,
  end: number,
): number | undefined {
  let max = Number.NEGATIVE_INFINITY;
  let seen = false;
  for (let index = start; index <= end; index += 1) {
    const value = values[index];
    if (value === undefined) {
      continue;
    }
    if (value > max) {
      max = value;
    }
    seen = true;
  }
  return seen ? max : undefined;
}

function smaSeries(values: (number | undefined)[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (length <= 0) {
    return output;
  }

  let sum = 0;
  let valid = 0;
  for (let index = 0; index < values.length; index += 1) {
    const current = values[index];
    if (current !== undefined) {
      sum += current;
      valid += 1;
    }

    if (index >= length) {
      const dropped = values[index - length];
      if (dropped !== undefined) {
        sum -= dropped;
        valid -= 1;
      }
    }

    if (index >= length - 1 && valid === length) {
      output[index] = sum / length;
    }
  }

  return output;
}

function emaSeries(values: number[], length: number): number[] {
  const output: number[] = new Array(values.length).fill(0);
  if (!values.length || length <= 0) {
    return output;
  }

  const alpha = 2 / (length + 1);
  let ema = values[0];

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (index === 0) {
      ema = value;
    } else {
      ema = ema + alpha * (value - ema);
    }
    output[index] = ema;
  }

  return output;
}

function atrSeries(candles: Candle[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(candles.length).fill(undefined);
  if (!candles.length || length <= 0) {
    return output;
  }

  const trueRanges: number[] = new Array(candles.length).fill(0);
  trueRanges[0] = candles[0].high - candles[0].low;

  for (let index = 1; index < candles.length; index += 1) {
    const current = candles[index];
    const previousClose = candles[index - 1].close;
    const range = current.high - current.low;
    const highGap = Math.abs(current.high - previousClose);
    const lowGap = Math.abs(current.low - previousClose);
    trueRanges[index] = Math.max(range, highGap, lowGap);
  }

  if (candles.length < length) {
    return output;
  }

  let sum = 0;
  for (let index = 0; index < length; index += 1) {
    sum += trueRanges[index];
  }

  let atr = sum / length;
  output[length - 1] = atr;

  for (let index = length; index < candles.length; index += 1) {
    atr = (atr * (length - 1) + trueRanges[index]) / length;
    output[index] = atr;
  }

  return output;
}

function rsiSeries(values: number[], length: number): (number | undefined)[] {
  const output: (number | undefined)[] = new Array(values.length).fill(undefined);
  if (values.length <= length || length <= 0) {
    return output;
  }

  let gainSum = 0;
  let lossSum = 0;
  for (let index = 1; index <= length; index += 1) {
    const delta = values[index] - values[index - 1];
    if (delta >= 0) {
      gainSum += delta;
    } else {
      lossSum += -delta;
    }
  }

  let avgGain = gainSum / length;
  let avgLoss = lossSum / length;
  output[length] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  for (let index = length + 1; index < values.length; index += 1) {
    const delta = values[index] - values[index - 1];
    const gain = delta > 0 ? delta : 0;
    const loss = delta < 0 ? -delta : 0;

    avgGain = (avgGain * (length - 1) + gain) / length;
    avgLoss = (avgLoss * (length - 1) + loss) / length;
    output[index] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  return output;
}

function stochRsiSeries(
  closes: number[],
  rsiLength: number,
  stochLength: number,
  kSmoothing: number,
  dSmoothing: number,
): { k: (number | undefined)[]; d: (number | undefined)[] } {
  const rsi = rsiSeries(closes, rsiLength);
  const raw: (number | undefined)[] = new Array(closes.length).fill(undefined);

  for (let index = 0; index < closes.length; index += 1) {
    if (index < stochLength - 1) {
      continue;
    }

    const low = lowestOptionalInRange(rsi, index - stochLength + 1, index);
    const high = highestOptionalInRange(rsi, index - stochLength + 1, index);
    const current = rsi[index];
    if (low === undefined || high === undefined || current === undefined) {
      continue;
    }

    const span = high - low;
    raw[index] = span === 0 ? 50 : ((current - low) / span) * 100;
  }

  const k = smaSeries(raw, Math.max(1, kSmoothing));
  const d = smaSeries(k, Math.max(1, dSmoothing));
  return { k, d };
}

function bollingerBands(
  closes: number[],
  length: number,
  multiplier: number,
): {
  upper: (number | undefined)[];
  basis: (number | undefined)[];
  lower: (number | undefined)[];
} {
  const basis = smaSeries(closes, length);
  const upper: (number | undefined)[] = new Array(closes.length).fill(undefined);
  const lower: (number | undefined)[] = new Array(closes.length).fill(undefined);

  for (let index = 0; index < closes.length; index += 1) {
    const mean = basis[index];
    if (index < length - 1 || mean === undefined) {
      continue;
    }

    let variance = 0;
    for (let bar = index - length + 1; bar <= index; bar += 1) {
      const delta = closes[bar] - mean;
      variance += delta * delta;
    }

    const stdDev = Math.sqrt(variance / length);
    upper[index] = mean + multiplier * stdDev;
    lower[index] = mean - multiplier * stdDev;
  }

  return { upper, basis, lower };
}

function isPivotLow(values: number[], index: number, left: number, right: number): boolean {
  const center = values[index];
  for (let offset = 1; offset <= left; offset += 1) {
    if (center >= values[index - offset]) {
      return false;
    }
  }
  for (let offset = 1; offset <= right; offset += 1) {
    if (center > values[index + offset]) {
      return false;
    }
  }
  return true;
}

function isPivotHigh(values: number[], index: number, left: number, right: number): boolean {
  const center = values[index];
  for (let offset = 1; offset <= left; offset += 1) {
    if (center <= values[index - offset]) {
      return false;
    }
  }
  for (let offset = 1; offset <= right; offset += 1) {
    if (center < values[index + offset]) {
      return false;
    }
  }
  return true;
}

function collectPivots(
  values: number[],
  left: number,
  right: number,
  mode: 'high' | 'low',
): Pivot[] {
  const pivots: Pivot[] = [];
  for (let index = left; index < values.length - right; index += 1) {
    const isMatch =
      mode === 'high'
        ? isPivotHigh(values, index, left, right)
        : isPivotLow(values, index, left, right);
    if (!isMatch) {
      continue;
    }
    pivots.push({ index, value: values[index] });
  }
  return pivots;
}

function hasIndexInRange(set: Set<number>, start: number, end: number): boolean {
  for (let index = start; index <= end; index += 1) {
    if (set.has(index)) {
      return true;
    }
  }
  return false;
}

function buildRegularAndHiddenDivergences(
  highs: number[],
  lows: number[],
  oscillator: (number | undefined)[],
  left: number,
  right: number,
): ColorProSignal[] {
  const signals: ColorProSignal[] = [];
  const lowPivots = collectPivots(lows, left, right, 'low');
  const highPivots = collectPivots(highs, left, right, 'high');
  const minGap = 3;
  const minOscDelta = 3;
  const minPriceDelta = 0.0025;

  for (let index = 1; index < lowPivots.length; index += 1) {
    const previous = lowPivots[index - 1];
    const current = lowPivots[index];
    if (current.index - previous.index < minGap) {
      continue;
    }

    const prevOsc = oscillator[previous.index];
    const curOsc = oscillator[current.index];
    if (prevOsc === undefined || curOsc === undefined) {
      continue;
    }

    if (current.value < previous.value * (1 - minPriceDelta) && curOsc > prevOsc + minOscDelta) {
      signals.push({
        index: current.index,
        code: 'R',
        direction: 'bullish',
        price: current.value,
      });
      continue;
    }

    if (current.value > previous.value * (1 + minPriceDelta) && curOsc < prevOsc - minOscDelta) {
      signals.push({
        index: current.index,
        code: 'H',
        direction: 'bullish',
        price: current.value,
      });
    }
  }

  for (let index = 1; index < highPivots.length; index += 1) {
    const previous = highPivots[index - 1];
    const current = highPivots[index];
    if (current.index - previous.index < minGap) {
      continue;
    }

    const prevOsc = oscillator[previous.index];
    const curOsc = oscillator[current.index];
    if (prevOsc === undefined || curOsc === undefined) {
      continue;
    }

    if (current.value > previous.value * (1 + minPriceDelta) && curOsc < prevOsc - minOscDelta) {
      signals.push({
        index: current.index,
        code: 'R',
        direction: 'bearish',
        price: current.value,
      });
      continue;
    }

    if (current.value < previous.value * (1 - minPriceDelta) && curOsc > prevOsc + minOscDelta) {
      signals.push({
        index: current.index,
        code: 'H',
        direction: 'bearish',
        price: current.value,
      });
    }
  }

  return signals;
}

function buildBaseTrendStates(
  candles: Candle[],
  emaFast: number[],
  emaSlow: number[],
  rsi: (number | undefined)[],
  stochK: (number | undefined)[],
  atr: (number | undefined)[],
): ColorProBaseTrendState[] {
  const states: ColorProBaseTrendState[] = new Array(candles.length).fill('neutralNoTrend');

  for (let index = 0; index < candles.length; index += 1) {
    const candle = candles[index];
    const fast = emaFast[index];
    const slow = emaSlow[index];
    const r = rsi[index];
    const k = stochK[index];
    const atrValue = atr[index];
    const previousFast = index > 0 ? emaFast[index - 1] : undefined;
    const previousState = index > 0 ? states[index - 1] : 'neutralNoTrend';

    let upScore = 0;
    let downScore = 0;

    if (fast > slow) {
      upScore += 1;
    }
    if (fast < slow) {
      downScore += 1;
    }
    if (candle.close >= fast) {
      upScore += 1;
    }
    if (candle.close <= fast) {
      downScore += 1;
    }
    if (previousFast !== undefined && fast > previousFast) {
      upScore += 1;
    }
    if (previousFast !== undefined && fast < previousFast) {
      downScore += 1;
    }
    if (r !== undefined && r >= 55) {
      upScore += 1;
    }
    if (r !== undefined && r <= 45) {
      downScore += 1;
    }
    if (k !== undefined && k >= 55) {
      upScore += 1;
    }
    if (k !== undefined && k <= 45) {
      downScore += 1;
    }

    const gapByAtr =
      atrValue !== undefined && atrValue > 0
        ? Math.abs(fast - slow) / atrValue
        : ratio(Math.abs(fast - slow), Math.max(1e-9, candle.close));

    let nextState: ColorProBaseTrendState = 'neutralNoTrend';

    if (upScore >= 4 && gapByAtr >= 0.28) {
      nextState = 'bullishTrendStrong';
    } else if (downScore >= 4 && gapByAtr >= 0.22) {
      nextState = 'bearishTrend';
    } else if (upScore >= 3 && gapByAtr >= 0.14) {
      nextState = 'bullishTrendWeak';
    } else if (downScore >= 3 && gapByAtr >= 0.12) {
      nextState = 'bearishTrend';
    }

    if (nextState === 'neutralNoTrend') {
      if (
        previousState === 'bullishTrendStrong' &&
        upScore >= 2 &&
        candle.close >= fast * 0.995
      ) {
        nextState = 'bullishTrendWeak';
      } else if (
        previousState === 'bullishTrendWeak' &&
        upScore >= 2 &&
        candle.close >= fast * 0.99
      ) {
        nextState = 'bullishTrendWeak';
      } else if (
        previousState === 'bearishTrend' &&
        downScore >= 2 &&
        candle.close <= fast * 1.005
      ) {
        nextState = 'bearishTrend';
      }
    }

    if (nextState === 'bearishTrend' && fast > slow && candle.close >= slow) {
      nextState = 'neutralNoTrend';
    }

    states[index] = nextState;
  }

  return states;
}

function buildYellowCandidates(
  candles: Candle[],
  lows: number[],
  baseStates: ColorProBaseTrendState[],
  stochK: (number | undefined)[],
  rsi: (number | undefined)[],
  bollingerLower: (number | undefined)[],
): Set<number> {
  const yellow = new Set<number>();
  const lookback = 8;
  const cooldownBars = 2;
  let lastYellowIndex = -cooldownBars;

  for (let index = 1; index < candles.length; index += 1) {
    if (index - lastYellowIndex < cooldownBars) {
      continue;
    }

    const candle = candles[index];
    const previous = candles[index - 1];
    const k = stochK[index];
    const r = rsi[index];
    const lower = bollingerLower[index];
    const range = candle.high - candle.low;
    const closeInUpperHalf = range <= 0 ? false : candle.close >= candle.low + range * 0.52;
    const inLowerBollingerContext = isLowerBollingerContext(candle, lower);

    let bearishPressureBars = 0;
    for (let bar = Math.max(1, index - 6); bar < index; bar += 1) {
      if (candles[bar].close <= candles[bar - 1].close) {
        bearishPressureBars += 1;
      }
    }

    const baseState = baseStates[index];
    const downContext = baseState === 'bearishTrend' || bearishPressureBars >= 3;
    const buyersShowing = candle.close > candle.open && candle.close > previous.close;
    const reversalBody = bodyRatio(candle) >= 0.2 && bodyRatio(candle) <= 0.78;
    const oversold =
      (k !== undefined && k <= 46) ||
      (r !== undefined && r <= 43) ||
      (lower !== undefined && candle.low <= lower * 1.01);

    const start = Math.max(0, index - lookback);
    const recentLow = lowestNumberInRange(lows, start, index - 1);
    const freshSelloff = candle.low <= recentLow * 1.01;

    if (
      downContext &&
      buyersShowing &&
      reversalBody &&
      oversold &&
      freshSelloff &&
      inLowerBollingerContext &&
      closeInUpperHalf
    ) {
      yellow.add(index);
      lastYellowIndex = index;
    }
  }

  return yellow;
}

function buildBlueBreakouts(
  candles: Candle[],
  highs: number[],
  baseStates: ColorProBaseTrendState[],
  stochK: (number | undefined)[],
  atr: (number | undefined)[],
  volumeEma: number[],
  useBreakoutVolumeFilter: boolean,
  breakoutVolumeMultiplier: number,
): Set<number> {
  const breakouts = new Set<number>();
  const lookback = 8;
  const cooldownBars = 24;
  let lastBreakout = -cooldownBars;

  for (let index = lookback; index < candles.length; index += 1) {
    if (index - lastBreakout < cooldownBars) {
      continue;
    }

    const candle = candles[index];
    const atrValue = atr[index];
    const k = stochK[index];
    const previousK = index > 0 ? stochK[index - 1] : undefined;

    if (atrValue === undefined || k === undefined || previousK === undefined) {
      continue;
    }

    let downPressureBars = 0;
    for (let bar = index - lookback; bar < index; bar += 1) {
      if (baseStates[bar] === 'bearishTrend' || candles[bar].close <= candles[bar - 1].close) {
        downPressureBars += 1;
      }
    }

    const resistance = highestNumberInRange(highs, index - lookback, index - 1);
    const range = candle.high - candle.low;
    const isLargeBullish = candle.close > candle.open && bodyRatio(candle) >= 0.72;
    const rangeExpansion = range >= atrValue * 1.4;
    const brokeResistance = candle.close > resistance * 1.004;
    const momentumLift = k >= 62 && k - previousK >= 8;
    const volumeConfirmed =
      !useBreakoutVolumeFilter ||
      candle.volume >= volumeEma[index] * breakoutVolumeMultiplier * 1.1;

    if (
      downPressureBars >= 6 &&
      isLargeBullish &&
      rangeExpansion &&
      brokeResistance &&
      momentumLift &&
      volumeConfirmed
    ) {
      breakouts.add(index);
      lastBreakout = index;
    }
  }

  return breakouts;
}

function buildPurpleBreakdowns(
  candles: Candle[],
  lows: number[],
  baseStates: ColorProBaseTrendState[],
  stochK: (number | undefined)[],
  atr: (number | undefined)[],
  volumeEma: number[],
  useBreakoutVolumeFilter: boolean,
  breakoutVolumeMultiplier: number,
): Set<number> {
  const breakdowns = new Set<number>();
  const lookback = 8;
  const cooldownBars = 20;
  let lastBreakdown = -cooldownBars;

  for (let index = lookback; index < candles.length; index += 1) {
    if (index - lastBreakdown < cooldownBars) {
      continue;
    }

    const candle = candles[index];
    const atrValue = atr[index];
    const k = stochK[index];
    const previousK = index > 0 ? stochK[index - 1] : undefined;
    if (atrValue === undefined || k === undefined || previousK === undefined) {
      continue;
    }

    let upPressureBars = 0;
    for (let bar = index - lookback; bar < index; bar += 1) {
      if (
        baseStates[bar] === 'bullishTrendStrong' ||
        baseStates[bar] === 'bullishTrendWeak' ||
        candles[bar].close >= candles[bar - 1].close
      ) {
        upPressureBars += 1;
      }
    }

    const support = lowestNumberInRange(lows, index - lookback, index - 1);
    const range = candle.high - candle.low;
    const isLargeBearish = candle.close < candle.open && bodyRatio(candle) >= 0.72;
    const rangeExpansion = range >= atrValue * 1.35;
    const brokeSupport = candle.close < support * 0.996;
    const momentumDrop = k <= 38 && previousK - k >= 8;
    const volumeConfirmed =
      !useBreakoutVolumeFilter ||
      candle.volume >= volumeEma[index] * breakoutVolumeMultiplier * 1.05;

    if (
      upPressureBars >= 5 &&
      isLargeBearish &&
      rangeExpansion &&
      brokeSupport &&
      momentumDrop &&
      volumeConfirmed
    ) {
      breakdowns.add(index);
      lastBreakdown = index;
    }
  }

  return breakdowns;
}

function buildBearishPivotSignals(
  candles: Candle[],
  highs: number[],
  baseStates: ColorProBaseTrendState[],
  stochK: (number | undefined)[],
  rsi: (number | undefined)[],
  bollingerUpper: (number | undefined)[],
  left: number,
  right: number,
): ColorProSignal[] {
  const pivots = collectPivots(highs, left, right, 'high');
  const signals: ColorProSignal[] = [];
  let lastSignalIndex = -100;

  for (const pivot of pivots) {
    const index = pivot.index;
    if (index < 1 || index + 1 >= candles.length) {
      continue;
    }
    if (index - lastSignalIndex < 4) {
      continue;
    }

    const candle = candles[index];
    const k = stochK[index];
    const previousK = stochK[index - 1];
    const r = rsi[index];
    const upper = bollingerUpper[index];
    const upContext =
      baseStates[index] === 'bullishTrendStrong' ||
      baseStates[index] === 'bullishTrendWeak' ||
      candle.close >= candles[index - 1].close;
    const overbought =
      (k !== undefined && k >= 68) ||
      (r !== undefined && r >= 62) ||
      (upper !== undefined && candle.high >= upper * 0.998);
    const momentumSlowing =
      previousK !== undefined && k !== undefined ? k <= previousK : candle.close < candle.open;

    let bearishFollowThrough = 0;
    let closesBelowPivotClose = 0;
    for (let step = 1; step <= 3; step += 1) {
      const follow = candles[index + step];
      if (!follow) {
        break;
      }
      if (follow.close < follow.open) {
        bearishFollowThrough += 1;
      }
      if (follow.close < candle.close) {
        closesBelowPivotClose += 1;
      }
    }

    if (
      upContext &&
      overbought &&
      momentumSlowing &&
      bearishFollowThrough >= 1 &&
      closesBelowPivotClose >= 2
    ) {
      signals.push({
        index: Math.min(candles.length - 1, index + 1),
        code: 'B',
        direction: 'bearish',
        price: candle.high,
      });
      lastSignalIndex = index;
    }
  }

  return signals;
}

function buildBearishBiasActive(candles: Candle[], bearishPivots: ColorProSignal[]): boolean[] {
  const active = new Array(candles.length).fill(false);

  for (const signal of bearishPivots) {
    const pivotIndex = signal.index;
    const pivotCandle = candles[pivotIndex];
    if (!pivotCandle) {
      continue;
    }

    const bodyTop = Math.max(pivotCandle.open, pivotCandle.close);

    for (let index = pivotIndex + 1; index <= pivotIndex + 3 && index < candles.length; index += 1) {
      if (candles[index].close > bodyTop) {
        break;
      }
      active[index] = true;
    }
  }

  return active;
}

function buildNeutralPivotMinorBullishHints(
  candles: Candle[],
  highs: number[],
  lows: number[],
  baseStates: ColorProBaseTrendState[],
  stochK: (number | undefined)[],
  atr: (number | undefined)[],
  yellowCandidates: Set<number>,
  blueBreakouts: Set<number>,
  left: number,
  right: number,
): Set<number> {
  const hints = new Set<number>();
  const pivots = collectPivots(lows, left, right, 'low');
  const cooldownBars = 32;
  let lastHintIndex = -cooldownBars;

  for (const pivot of pivots) {
    const index = pivot.index;
    if (index - lastHintIndex < cooldownBars) {
      continue;
    }

    const candle = candles[index];
    const k = stochK[index];
    const atrValue = atr[index];
    if (k === undefined || atrValue === undefined) {
      continue;
    }

    if (k < 18 || k > 56) {
      continue;
    }

    if (hasIndexInRange(yellowCandidates, Math.max(0, index - 2), Math.min(candles.length - 1, index + 2))) {
      continue;
    }
    if (hasIndexInRange(blueBreakouts, Math.max(0, index - 8), Math.min(candles.length - 1, index + 2))) {
      continue;
    }

    let priorBullBars = 0;
    for (let bar = Math.max(0, index - 6); bar < index; bar += 1) {
      if (baseStates[bar] === 'bullishTrendStrong' || baseStates[bar] === 'bullishTrendWeak') {
        priorBullBars += 1;
      }
    }

    let recentBearCandles = 0;
    for (let bar = Math.max(0, index - 3); bar <= index; bar += 1) {
      if (candles[bar].close < candles[bar].open) {
        recentBearCandles += 1;
      }
    }

    if (index < 8) {
      continue;
    }
    const recentHigh = highestNumberInRange(highs, index - 8, index - 1);
    const drawdown = recentHigh - candle.low;
    const transitionDown = priorBullBars >= 4 && recentBearCandles >= 2 && drawdown >= atrValue * 1.4;
    if (!transitionDown) {
      continue;
    }

    const indecision = bodyRatio(candle) <= 0.28 || lowerWickRatio(candle) >= 0.45;
    const nonImpulse = bodyRatio(candle) <= 0.45 && upperWickRatio(candle) <= 0.45;
    if (!indecision || !nonImpulse) {
      continue;
    }

    hints.add(index);
    lastHintIndex = index;
  }

  return hints;
}

function buildConfirmedReversals(
  candles: Candle[],
  highs: number[],
  stochK: (number | undefined)[],
  stochD: (number | undefined)[],
  yellowCandidates: Set<number>,
  blueBreakouts: Set<number>,
): ColorProSignal[] {
  const signals: ColorProSignal[] = [];
  let lastSignalIndex = -100;

  for (let index = 2; index < candles.length; index += 1) {
    if (index - lastSignalIndex < 4) {
      continue;
    }
    if (blueBreakouts.has(index)) {
      continue;
    }

    const latestYellowIndex = index - 1;
    if (latestYellowIndex >= 0 && yellowCandidates.has(latestYellowIndex)) {
      continue;
    }

    if (!hasIndexInRange(yellowCandidates, Math.max(0, index - 4), index - 2)) {
      continue;
    }

    const candle = candles[index];
    const k = stochK[index];
    const d = stochD[index];
    const previousK = stochK[index - 1];
    const previousD = stochD[index - 1];

    if (k === undefined || d === undefined || previousK === undefined || previousD === undefined) {
      continue;
    }

    const previousHigh = highestNumberInRange(highs, index - 3, index - 1);
    const breaksNearResistance = candle.close > previousHigh * 1.001;
    const oscillatorTurn = previousK <= previousD && k > d && k >= 45;
    const bodyQualified = candle.close > candle.open && bodyRatio(candle) >= 0.35;

    if (breaksNearResistance && oscillatorTurn && bodyQualified) {
      signals.push({
        index,
        code: 'C',
        direction: 'bullish',
        price: candle.low,
      });
      lastSignalIndex = index;
    }
  }

  return signals;
}

function buildStalledAfterBreakoutIndexes(
  candles: Candle[],
  baseStates: ColorProBaseTrendState[],
  stochK: (number | undefined)[],
  blueBreakouts: Set<number>,
): Set<number> {
  const stalled = new Set<number>();

  for (let index = 1; index < candles.length; index += 1) {
    const k = stochK[index];
    const previousK = stochK[index - 1];
    const hasRecentBlue = hasIndexInRange(blueBreakouts, Math.max(0, index - 4), index - 1);
    const baseBullish =
      baseStates[index] === 'bullishTrendStrong' || baseStates[index] === 'bullishTrendWeak';
    const momentumDropping =
      previousK !== undefined && k !== undefined ? k <= previousK - 4 : candles[index].close <= candles[index].open;
    const closeNotAdvancing = candles[index].close <= candles[index - 1].close * 1.002;
    const smallBody = bodyRatio(candles[index]) <= 0.45;

    if (hasRecentBlue && baseBullish && momentumDropping && closeNotAdvancing && smallBody) {
      stalled.add(index);
    }
  }

  return stalled;
}

function buildCandleStates(
  candles: Candle[],
  baseStates: ColorProBaseTrendState[],
  emaFast: number[],
  emaSlow: number[],
  stochK: (number | undefined)[],
  bollingerLower: (number | undefined)[],
  yellowCandidates: Set<number>,
  blueBreakouts: Set<number>,
  purpleBreakdowns: Set<number>,
  neutralPivotHints: Set<number>,
  bearishPivotSignals: ColorProSignal[],
  bearishBiasActive: boolean[],
  confirmedReversals: ColorProSignal[],
  allowUpperBandBridgeYellow: boolean,
): ColorProCandleState[] {
  const states: ColorProCandleState[] = new Array(candles.length).fill('neutralNoTrend');
  const bearishPivotIndexes = new Set(bearishPivotSignals.map((signal) => signal.index));
  const confirmedReversalIndexes = new Set(confirmedReversals.map((signal) => signal.index));
  const stalledIndexes = buildStalledAfterBreakoutIndexes(candles, baseStates, stochK, blueBreakouts);
  let needsYellowBeforeGreen = false;

  const assignState = (index: number, requestedState: ColorProCandleState): void => {
    const previousState = index > 0 ? states[index - 1] : undefined;
    const requestIsGreen = GREEN_FAMILY_STATES.has(requestedState);
    const forceGreyBridgeYellow = requestIsGreen && previousState === 'neutralNoTrend';
    const forceBridgeYellow = (requestIsGreen && needsYellowBeforeGreen) || forceGreyBridgeYellow;
    const guardedState = applyColorProV31StateGuards(
      previousState,
      forceBridgeYellow ? 'bullishUnconfirmedReversal' : requestedState,
      candles[index],
      bollingerLower[index],
      forceBridgeYellow
        ? {
            allowOffBandYellow: forceGreyBridgeYellow ? true : allowUpperBandBridgeYellow,
          }
        : undefined,
    );

    states[index] = guardedState;

    if (RED_FAMILY_STATES.has(guardedState)) {
      needsYellowBeforeGreen = true;
      return;
    }

    if (guardedState === 'bullishUnconfirmedReversal') {
      needsYellowBeforeGreen = false;
    }
  };

  for (let index = 0; index < candles.length; index += 1) {
    const baseState = baseStates[index];
    const inEmaBullRegime = emaFast[index] > emaSlow[index] && candles[index].close >= emaSlow[index];

    if (bearishPivotIndexes.has(index)) {
      assignState(index, 'bearishPivot');
      continue;
    }

    if (blueBreakouts.has(index)) {
      assignState(index, 'bullishBreakout');
      continue;
    }

    if (purpleBreakdowns.has(index)) {
      assignState(index, 'bearishBreakdown');
      continue;
    }

    if (confirmedReversalIndexes.has(index)) {
      assignState(index, baseState === 'bullishTrendStrong' ? 'bullishTrendStrong' : 'bullishTrendWeak');
      continue;
    }

    if (yellowCandidates.has(index)) {
      assignState(index, 'bullishUnconfirmedReversal');
      continue;
    }

    if (neutralPivotHints.has(index)) {
      assignState(index, 'neutralPivotMinorBullishBias');
      continue;
    }

    if (stalledIndexes.has(index)) {
      assignState(index, 'stalledAfterBreakout');
      continue;
    }

    if (
      bearishBiasActive[index] &&
      !inEmaBullRegime &&
      baseState !== 'bullishTrendStrong' &&
      baseState !== 'bullishTrendWeak'
    ) {
      assignState(index, 'bearishTrend');
      continue;
    }

    assignState(index, baseState);
  }

  return states;
}

function buildPaintEligibility(
  candles: Candle[],
  atr: (number | undefined)[],
  rsi: (number | undefined)[],
  stochK: (number | undefined)[],
  stochD: (number | undefined)[],
): boolean[] {
  const eligible = new Array(candles.length).fill(false);
  for (let index = 1; index < candles.length; index += 1) {
    eligible[index] =
      atr[index] !== undefined &&
      rsi[index] !== undefined &&
      stochK[index] !== undefined &&
      stochD[index] !== undefined;
  }
  return eligible;
}

function dedupeSignals(signals: ColorProSignal[]): ColorProSignal[] {
  const unique = new Map<string, ColorProSignal>();

  for (const signal of signals) {
    const key = `${signal.index}:${signal.code}:${signal.direction}`;
    if (!unique.has(key)) {
      unique.set(key, signal);
    }
  }

  return Array.from(unique.values()).sort((a, b) => {
    if (a.index !== b.index) {
      return a.index - b.index;
    }
    return a.code.localeCompare(b.code);
  });
}

function toUtcDayKey(isoTime: string): string {
  return isoTime.slice(0, 10);
}

function toUtcWeekKey(isoTime: string): string {
  const date = new Date(isoTime);
  const day = (date.getUTCDay() + 6) % 7;
  const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  monday.setUTCDate(monday.getUTCDate() - day);
  return monday.toISOString().slice(0, 10);
}

function computePeriodLevels(candles: Candle[], periodKey: (isoTime: string) => string): PeriodLevels {
  const resistance: (number | undefined)[] = new Array(candles.length).fill(undefined);
  const support: (number | undefined)[] = new Array(candles.length).fill(undefined);
  const pivot: (number | undefined)[] = new Array(candles.length).fill(undefined);

  let currentKey: string | undefined;
  let currentHigh = Number.NEGATIVE_INFINITY;
  let currentLow = Number.POSITIVE_INFINITY;
  let previousHigh: number | undefined;
  let previousLow: number | undefined;
  let previousClose: number | undefined;

  for (let index = 0; index < candles.length; index += 1) {
    const candle = candles[index];
    const key = periodKey(candle.time);

    if (currentKey === undefined) {
      currentKey = key;
    }

    if (key !== currentKey) {
      previousHigh = currentHigh;
      previousLow = currentLow;
      previousClose = candles[index - 1]?.close;

      currentKey = key;
      currentHigh = Number.NEGATIVE_INFINITY;
      currentLow = Number.POSITIVE_INFINITY;
    }

    resistance[index] = previousHigh;
    support[index] = previousLow;
    pivot[index] =
      previousHigh !== undefined && previousLow !== undefined && previousClose !== undefined
        ? (previousHigh + previousLow + previousClose) / 3
        : undefined;

    if (candle.high > currentHigh) {
      currentHigh = candle.high;
    }
    if (candle.low < currentLow) {
      currentLow = candle.low;
    }
  }

  return { resistance, support, pivot };
}

export function computeColorProTradingSuiteV31Overlay({
  candles,
  rsiLength = DEFAULT_RSI_LENGTH,
  stochLength = DEFAULT_STOCH_LENGTH,
  kSmoothing = DEFAULT_K_SMOOTHING,
  dSmoothing = DEFAULT_D_SMOOTHING,
  bollingerLength = DEFAULT_BOLLINGER_LENGTH,
  bollingerMultiplier = DEFAULT_BOLLINGER_MULTIPLIER,
  useBreakoutVolumeFilter = DEFAULT_USE_BREAKOUT_VOLUME_FILTER,
  breakoutVolumeMultiplier = DEFAULT_BREAKOUT_VOLUME_MULTIPLIER,
  allowUpperBandBridgeYellow = DEFAULT_ALLOW_UPPER_BAND_BRIDGE_YELLOW,
}: ColorProTradingSuiteV31Params): ColorProTradingSuiteV31OverlayResult | null {
  if (candles.length < 60) {
    return null;
  }

  const closes = candles.map((candle) => candle.close);
  const highs = candles.map((candle) => candle.high);
  const lows = candles.map((candle) => candle.low);
  const volumes = candles.map((candle) => candle.volume);

  const emaFast = emaSeries(closes, 21);
  const emaSlow = emaSeries(closes, 89);
  const atr = atrSeries(candles, DEFAULT_ATR_LENGTH);
  const volumeEma = emaSeries(volumes, DEFAULT_BREAKOUT_VOLUME_EMA_LENGTH);
  const rsi = rsiSeries(closes, rsiLength);
  const { k, d } = stochRsiSeries(closes, rsiLength, stochLength, kSmoothing, dSmoothing);
  const bollinger = bollingerBands(closes, bollingerLength, bollingerMultiplier);
  const normalizedBreakoutVolumeMultiplier = Math.max(0.1, breakoutVolumeMultiplier);

  const baseStates = buildBaseTrendStates(candles, emaFast, emaSlow, rsi, k, atr);
  const yellowCandidates = buildYellowCandidates(
    candles,
    lows,
    baseStates,
    k,
    rsi,
    bollinger.lower,
  );
  const blueBreakouts = buildBlueBreakouts(
    candles,
    highs,
    baseStates,
    k,
    atr,
    volumeEma,
    useBreakoutVolumeFilter,
    normalizedBreakoutVolumeMultiplier,
  );
  const purpleBreakdowns = buildPurpleBreakdowns(
    candles,
    lows,
    baseStates,
    k,
    atr,
    volumeEma,
    useBreakoutVolumeFilter,
    normalizedBreakoutVolumeMultiplier,
  );

  const bearishPivots = buildBearishPivotSignals(
    candles,
    highs,
    baseStates,
    k,
    rsi,
    bollinger.upper,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const confirmedReversals = buildConfirmedReversals(candles, highs, k, d, yellowCandidates, blueBreakouts);
  const divergenceSignals = buildRegularAndHiddenDivergences(
    highs,
    lows,
    rsi,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );
  const neutralPivotHints = buildNeutralPivotMinorBullishHints(
    candles,
    highs,
    lows,
    baseStates,
    k,
    atr,
    yellowCandidates,
    blueBreakouts,
    DEFAULT_PIVOT_LEFT,
    DEFAULT_PIVOT_RIGHT,
  );

  const bearishBiasActive = buildBearishBiasActive(candles, bearishPivots);
  const candleStates = buildCandleStates(
    candles,
    baseStates,
    emaFast,
    emaSlow,
    k,
    bollinger.lower,
    yellowCandidates,
    blueBreakouts,
    purpleBreakdowns,
    neutralPivotHints,
    bearishPivots,
    bearishBiasActive,
    confirmedReversals,
    allowUpperBandBridgeYellow,
  );
  const paintEligibility = buildPaintEligibility(candles, atr, rsi, k, d);
  const candleColors = candleStates.map((state, index) =>
    paintEligibility[index] ? COLORPRO_V31_CANDLE_COLORS[state] : undefined,
  );

  const signals = dedupeSignals([...divergenceSignals, ...bearishPivots, ...confirmedReversals]);
  const dailyLevels = computePeriodLevels(candles, toUtcDayKey);
  const weeklyLevels = computePeriodLevels(candles, toUtcWeekKey);

  const totalB = signals.filter((signal) => signal.code === 'B').length;
  const totalC = signals.filter((signal) => signal.code === 'C').length;
  const totalR = signals.filter((signal) => signal.code === 'R').length;
  const totalH = signals.filter((signal) => signal.code === 'H').length;
  const blueCount = candleStates.filter((state) => state === 'bullishBreakout').length;
  const brightPurpleCount = candleStates.filter(
    (state) => state === 'neutralPivotMinorBullishBias',
  ).length;
  const unpaintedCandles = candleColors.filter((color) => color === undefined).length;

  return {
    candleStates,
    candleColors,
    bollingerUpper: bollinger.upper,
    bollingerBasis: bollinger.basis,
    bollingerLower: bollinger.lower,
    dailyResistance: dailyLevels.resistance,
    dailySupport: dailyLevels.support,
    weeklyResistance: weeklyLevels.resistance,
    weeklySupport: weeklyLevels.support,
    pivotPoints: dailyLevels.pivot,
    signals,
    info:
      `ColorPro v3.1 adaptive: B ${totalB} | C ${totalC} | R ${totalR} | H ${totalH} | ` +
      `Blue ${blueCount} | BrightPurple ${brightPurpleCount} | Unpainted ${unpaintedCandles}`,
  };
}
